import { useState } from 'react'
import { createRoute, Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetInvestigationTranslationBreakdown } from '@/application/services/translation.service'
import { useGetLeadsByInvestigation } from '@/application/services/lead.service'
import { useGetInvestigationForEdit } from '@/application/services/investigation.service'
import type { Lead } from '@/domains/types'
import { Breadcrumb } from '@/ui/components/common/Breadcrumb'
import { Button } from '@/ui/components/common/Button'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { Card, CardContent } from '@/ui/components/common/Card'
import { InvestigationPublicUrls } from '@/ui/pages/management/investigations/edit/components/InvestigationPublicUrls'
import { EditLeadDialog } from '@/ui/components/leads/EditLeadDialog'
import { translationsRoute } from '@/ui/pages/management/translations'
import {
  TranslationCompletenessTable,
  type TranslationCompletenessRow,
} from '../components/TranslationCompletenessTable'
import { TranslationEntityEditAction } from '../components/TranslationEntityEditAction'

export const translationsInvestigationDetailRoute = createRoute({
  getParentRoute: () => translationsRoute,
  path: '$investigationId',
  component: TranslationInvestigationDetailPage,
})

export function TranslationInvestigationDetailPage() {
  const { t } = useTranslation()
  const { investigationId: routeInvestigationId } = useParams({ strict: false })
  const investigationId = routeInvestigationId ?? ''
  const { data: breakdown, isLoading: isLoadingBreakdown } =
    useGetInvestigationTranslationBreakdown(investigationId)
  const { data: leads, isLoading: isLoadingLeads } = useGetLeadsByInvestigation(investigationId)
  const { data: investigation } = useGetInvestigationForEdit(investigationId)
  const [editingLead, setEditingLead] = useState<Lead | null>(null)

  const isLoading = isLoadingBreakdown || isLoadingLeads

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!breakdown) {
    return (
      <div className="space-y-4">
        <Link to="/manage/translations">
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            {t('translations.detail_back_to_dashboard')}
          </Button>
        </Link>
        <p className="text-muted-foreground">{t('translations.detail_not_found')}</p>
      </div>
    )
  }

  const leadsById = new Map((leads ?? []).map((lead) => [lead.id, lead]))

  const rows: TranslationCompletenessRow[] = breakdown.leads.map((leadNode) => {
    const lead = leadsById.get(leadNode.id) ?? null
    if (!lead && import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only diagnostic, never rendered in production
      console.warn(
        `[translations] lead ${leadNode.id} is in the breakdown but not in useGetLeadsByInvestigation — edit action disabled for this row`,
      )
    }
    return {
      id: leadNode.id,
      label: leadNode.label,
      locales: leadNode.aggregate,
      linkTo: {
        to: '/manage/translations/$investigationId/leads/$leadId',
        params: { investigationId, leadId: leadNode.id },
      },
      actions: lead ? (
        <TranslationEntityEditAction
          onEdit={() => setEditingLead(lead)}
          ariaLabel={t('translations.detail_edit_lead_aria', { title: leadNode.label })}
        />
      ) : undefined,
    }
  })

  const investigationOwnRow: TranslationCompletenessRow = {
    id: breakdown.investigation.id,
    label: t('translations.detail_row_investigation_own'),
    locales: breakdown.investigation.own,
    emphasis: true,
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: t('translations.dashboard_title'), href: '/manage/translations' },
          { label: breakdown.investigation.label },
        ]}
      />

      <h2 className="text-2xl font-bold">{breakdown.investigation.label}</h2>

      <Card>
        <CardContent className="space-y-3">
          <TranslationCompletenessTable
            labelHeader={t('translations.detail_col_lead')}
            emptyMessage={t('translations.detail_leads_empty')}
            isLoading={false}
            rows={[investigationOwnRow, ...rows]}
          />
          {breakdown.leads.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('translations.detail_leads_empty')}</p>
          )}
        </CardContent>
      </Card>

      {investigation && (
        <InvestigationPublicUrls
          slugs={investigation.slugs}
          sourceLocale={investigation.sourceLocale}
        />
      )}

      <EditLeadDialog
        lead={editingLead}
        open={editingLead !== null}
        onOpenChange={(open) => {
          if (!open) setEditingLead(null)
        }}
      />
    </div>
  )
}
